import { useEffect, useState } from 'react'
import { publicApi, getStoredPublicAgentSession, setStoredPublicAgentSession } from '../api/client'
import { executeRecaptcha } from '../lib/recaptcha'
import type { Customer, PublicAgentConversation, PublicAgentMessage } from '../api/types'

let optimisticId = -1

export function usePublicAgentChat(customer: Customer | null) {
  const [conversation, setConversation] = useState<PublicAgentConversation | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true
    const stored = getStoredPublicAgentSession()
    setIsLoading(true)

    void (async () => {
      const recaptchaToken = await executeRecaptcha('public_agent_session')
      const headers: Record<string, string> = {}
      if (recaptchaToken) headers['X-Recaptcha-Token'] = recaptchaToken
      try {
        const response = await publicApi.post<PublicAgentConversation>(
          '/public-agent/web/sessions/',
          stored?.web_session_id ? { web_session_id: stored.web_session_id } : {},
          { headers },
        )
        if (!mounted) return
        if (response.status === 204 || !response.data) {
          setError('Chat no disponible.')
          return
        }
        setConversation(response.data)
        setStoredPublicAgentSession({
          id: response.data.id,
          web_session_id: response.data.web_session_id,
        })
        setError(null)
      } catch {
        if (!mounted) return
        setError('No se pudo iniciar el chat.')
      } finally {
        if (mounted) setIsLoading(false)
      }
    })()

    return () => {
      mounted = false
    }
  }, [])

  const sendMessage = async (content: string) => {
    if (!conversation?.id) return
    setIsSending(true)
    setError(null)

    // Optimistic update: show user message immediately
    const optimisticMsg: PublicAgentMessage = {
      id: optimisticId--,
      direction: 'inbound',
      sender_type: 'guest',
      content,
      created_at: new Date().toISOString(),
      metadata: {},
    }
    setConversation((prev) =>
      prev ? { ...prev, messages: [...prev.messages, optimisticMsg] } : prev,
    )

    try {
      const response = await publicApi.post<PublicAgentConversation>(
        `/public-agent/web/sessions/${conversation.id}/messages/`,
        { content },
      )
      setConversation(response.data)
    } catch {
      setError('No se pudo enviar el mensaje.')
      // Remove optimistic message on error
      setConversation((prev) =>
        prev
          ? { ...prev, messages: prev.messages.filter((m) => m.id !== optimisticMsg.id) }
          : prev,
      )
    } finally {
      setIsSending(false)
    }
  }

  const suggestPhone = async () => {
    if (!customer?.phone) return
    await sendMessage(customer.phone)
  }

  return {
    conversation,
    messages: conversation?.messages || [],
    isLoading,
    isSending,
    error,
    sendMessage,
    suggestPhone,
  }
}
